import { getApiOrigin } from "@utils/apiOrigin";

const BASE_URL = getApiOrigin();
const SIGNUP_INPUT_FORM_ENDPOINT = "/api/v1/signup-input-form";

/**
 * Bot mitigation (backend should enforce):
 * 1. contact_nickname: Honeypot – reject request if this field is non-empty.
 * 2. page_loaded_at: Client page load time (ms). Reject if submit happens too soon,
 *    e.g. (Date.now() - page_loaded_at) < 3000 (min 3 seconds on page).
 */
export interface SignupInputFormPayload {
  email: string;
  first_name: string;
  last_name: string;
  organization_name: string;
  facility_name: string;
  /** City entered by the user */
  city: string;
  /** State entered by the user */
  state: string;
  /** NPI number of the facility selected from search results; absent when user selected "not found" */
  npi_number?: string;
  /** Facility type display label resolved from NPI taxonomy; absent when user selected "not found" or type unknown */
  facility_type?: string;
  /** Kept for backward compatibility; always sent as empty string */
  facility_address: string;
  phone_number: string;
  /** Honeypot - backend should reject if non-empty */
  contact_nickname?: string;
  /** Page load timestamp (ms) - backend should reject if submit too soon (e.g. < 3s) */
  page_loaded_at?: number;
  /**
   * Canonical vertical slug chosen at signup (e.g. 'healthcare', 'aviation').
   * Persisted on signup_input_form.facility_details and
   * later copied to users.vertical when the account is created.
   */
  vertical?: string;
  facility_type_id?: number;
  /** Structured aviation targeting collected when vertical === "aviation". */
  aviation_targeting?: {
    primary_profile_slug?: string;
    activity_domains?: string[];
    legal_roles?: string[];
    operation_contexts?: string[];
    asset_types?: string[];
    certificates_or_approvals?: string[];
    risk_targets?: string[];
    regulation_packs?: string[];
    overlay_packs?: string[];
    penalty_packs?: string[];
  };
}

export interface SignupInputFormResponse {
  success: boolean;
  message: string;
  data?: {
    id: number;
    email: string;
    organization_name: string;
    facility_name: string;
    city: string;
    state: string;
    npi_number?: string;
    facility_address: string;
    phone_number: string;
    discount_coupon: string | null;
    is_verified: boolean;
    is_active: boolean;
    is_delete: boolean;
    created_at: string;
    updated_at: string;
  };
}

export async function submitSignupInputForm(
  payload: SignupInputFormPayload
): Promise<SignupInputFormResponse> {
  if (!BASE_URL) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const url = `${BASE_URL}${SIGNUP_INPUT_FORM_ENDPOINT}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      text || `Signup input form submission failed: ${response.status}`
    );
  }

  const data = (await response.json()) as SignupInputFormResponse;
  return data;
}


